// ============================================================================
//  <DataTable> — TanStack Table v8 + dnd-kit, with per-user persistence
// ============================================================================
//
//  Usage:
//
//    <DataTable
//      viewName="balances.main"
//      columns={[
//        { id: 'customer', header: 'Customer', accessorKey: 'customer', size: 220 },
//        { id: 'opening',  header: 'Opening',  accessorFn: r => r.opening, size: 110, meta: { align: 'right', format: 'money' } },
//        ...
//      ]}
//      data={rows}
//      defaultSort={[{ id: 'endingWithPending', desc: true }]}
//    />
//
//  Persists per (Entra user email, viewName) to Supabase via /api/user/preferences:
//    { columnOrder, columnSizing, sorting, columnVisibility }
//
//  Features:
//    - Click header to sort (toggles asc/desc, then unsort)
//    - Drag header to reorder columns
//    - Drag right edge of header to resize
//    - "Reset layout" button restores defaults
//
//  Cells render via either the column's `cell` render function OR the meta
//  format hint ('money', 'num', 'date', etc) for quick-and-dirty defaults.
// ============================================================================

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, flexRender,
} from '@tanstack/react-table';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, horizontalListSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const FMT_MONEY = (v) => v == null || isNaN(v) ? '—' : (v < 0 ? '−$' : '$') + Math.abs(Math.round(Number(v))).toLocaleString();
const FMT_NUM   = (v) => v == null ? '—' : Number(v).toLocaleString();
const FMT_DATE  = (v) => v == null ? '—' : String(v).slice(0, 10);

function renderCell(cell) {
  const v = cell.getValue();
  const fmt = cell.column.columnDef.meta && cell.column.columnDef.meta.format;
  if (cell.column.columnDef.cell) return flexRender(cell.column.columnDef.cell, cell.getContext());
  if (fmt === 'money') return FMT_MONEY(v);
  if (fmt === 'num')   return FMT_NUM(v);
  if (fmt === 'date')  return FMT_DATE(v);
  return v == null ? '' : String(v);
}

function DraggableHeader({ header, sort, allowReorder, isFirstCol }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.column.id,
    disabled: !allowReorder
  });
  const meta = header.column.columnDef.meta || {};
  const isResizing = header.column.getIsResizing();
  const thStyle = {
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? 'none' : 'transform 150ms ease, background 120ms ease',
    opacity: isDragging ? 0.7 : 1,
    width: header.getSize(),
    position: 'relative',
    whiteSpace: 'nowrap',
    background: isDragging ? '#f1f5f9' : '#fff',
    borderBottom: '2px solid #e2e8f0',
    borderRight: '1px solid #f1f5f9',
    padding: 0,
    fontWeight: 600,
    textAlign: meta.align === 'right' ? 'right' : 'left',
    userSelect: 'none',
    boxSizing: 'border-box',
  };
  // Inner content holds the drag listeners. Click on the resize handle
  // never reaches them — the th itself isn't draggable, only the inner area is.
  const innerStyle = {
    padding: '8px 14px 8px 12px',
    cursor: allowReorder ? 'grab' : 'pointer',
    paddingRight: 18,  // breathing room for the resize handle
  };
  const sorted = header.column.getIsSorted();
  return (
    <th ref={setNodeRef} style={thStyle} colSpan={header.colSpan} className={isFirstCol ? 'dt-sticky-col' : undefined}>
      <div {...attributes} {...listeners} style={innerStyle}>
        <span onClick={() => header.column.toggleSorting()}
              style={{ cursor: 'pointer' }}
              title="Click to sort. Drag header to reorder. Drag right edge to resize.">
          {flexRender(header.column.columnDef.header, header.getContext())}
          {sorted === 'asc' && ' ▲'}
          {sorted === 'desc' && ' ▼'}
        </span>
      </div>
      {/* Resize handle — 10px wide, clearly visible on hover, never triggers reorder */}
      <div
        onMouseDown={e => { e.stopPropagation(); header.getResizeHandler()(e); }}
        onTouchStart={e => { e.stopPropagation(); header.getResizeHandler()(e); }}
        onClick={e => e.stopPropagation()}
        onDoubleClick={e => { e.stopPropagation(); header.column.resetSize(); }}
        title="Drag to resize column. Double-click to reset width."
        className="dt-resize-handle"
        style={{
          position: 'absolute',
          right: 0, top: 0, bottom: 0,
          width: 10,
          cursor: 'col-resize',
          touchAction: 'none',
          // Centered 2px guide line that lights up on hover / while resizing
          background: isResizing
            ? 'linear-gradient(to right, transparent 0%, transparent 40%, #0369a1 40%, #0369a1 60%, transparent 60%, transparent 100%)'
            : 'transparent',
          transition: 'background 120ms ease',
          zIndex: 2
        }}
      />
    </th>
  );
}

async function loadPrefs(viewName) {
  try {
    const r = await fetch('/api/user/preferences/' + encodeURIComponent(viewName), { credentials: 'include' });
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.prefs;
  } catch (_) { return null; }
}

let _saveTimers = {};
function debouncedSavePrefs(viewName, prefs) {
  clearTimeout(_saveTimers[viewName]);
  _saveTimers[viewName] = setTimeout(async () => {
    try {
      await fetch('/api/user/preferences/' + encodeURIComponent(viewName), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(prefs)
      });
    } catch (_) { /* silent — UI not blocked on save */ }
  }, 500);
}

export default function DataTable({ viewName, columns, data, defaultSort, emptyMessage }) {
  const defaultOrder = useMemo(() => columns.map(c => c.id), [columns]);
  const [columnOrder,      setColumnOrder]      = useState(defaultOrder);
  const [columnSizing,     setColumnSizing]     = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting,          setSorting]          = useState(defaultSort || []);
  const [loaded, setLoaded] = useState(false);

  // Load prefs once on mount.
  useEffect(() => {
    let alive = true;
    loadPrefs(viewName).then(p => {
      if (!alive) return;
      if (p) {
        if (Array.isArray(p.columnOrder) && p.columnOrder.length) setColumnOrder(p.columnOrder.filter(id => defaultOrder.includes(id)).concat(defaultOrder.filter(id => !p.columnOrder.includes(id))));
        if (p.columnSizing && typeof p.columnSizing === 'object') setColumnSizing(p.columnSizing);
        if (p.columnVisibility && typeof p.columnVisibility === 'object') setColumnVisibility(p.columnVisibility);
        if (Array.isArray(p.sorting)) setSorting(p.sorting);
      }
      setLoaded(true);
    });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewName]);

  // Save prefs whenever they change (after initial load).
  useEffect(() => {
    if (!loaded) return;
    debouncedSavePrefs(viewName, { columnOrder, columnSizing, columnVisibility, sorting });
  }, [viewName, loaded, columnOrder, columnSizing, columnVisibility, sorting]);

  const table = useReactTable({
    data,
    columns,
    state: { columnOrder, columnSizing, columnVisibility, sorting },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnOrder(prev => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  function resetLayout() {
    setColumnOrder(defaultOrder);
    setColumnSizing({});
    setColumnVisibility({});
    setSorting(defaultSort || []);
  }

  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: '0.8em', color: '#666', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Drag headers to reorder · drag right edge to resize · click to sort.</span>
        <button onClick={resetLayout} style={{ padding: '2px 8px', fontSize: '0.85em', border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer', borderRadius: 3 }}>
          Reset layout
        </button>
      </div>
      <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #e5e7eb', borderRadius: 4 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: table.getCenterTotalSize(), minWidth: '100%' }}>
            <thead className="sticky-thead">
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>
                  <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                    {hg.headers.map((h, idx) => <DraggableHeader key={h.id} header={h} allowReorder={true} isFirstCol={idx === 0} />)}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 && (
                <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>{emptyMessage || 'No data.'}</td></tr>
              )}
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, idx) => {
                    const meta = cell.column.columnDef.meta || {};
                    const rowBg = meta.cellBg || (typeof meta.rowBg === 'function' ? meta.rowBg(row.original) : '');
                    return (
                      <td key={cell.id}
                          className={idx === 0 ? 'dt-sticky-col' : undefined}
                          style={{
                            padding: '6px 10px',
                            borderBottom: '1px solid #f1f5f9',
                            width: cell.column.getSize(),
                            textAlign: meta.align === 'right' ? 'right' : 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            background: rowBg || (idx === 0 ? '#fff' : ''),
                            ...(meta.cellStyle || {})
                          }}>
                        {renderCell(cell)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* Optional totals footer + second 'check' row.
                Renders columnDef.footer for the totals row and
                columnDef.footerCheck for an extra row beneath it. */}
            {(table.getAllColumns().some(c => c.columnDef.footer) ||
              table.getAllColumns().some(c => c.columnDef.footerCheck)) && (
              <tfoot>
                {table.getAllColumns().some(c => c.columnDef.footer) && (
                  <tr style={{ background: '#f3f4f6', borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                    {table.getVisibleLeafColumns().map((col, idx) => {
                      const meta = col.columnDef.meta || {};
                      const f = col.columnDef.footer;
                      return (
                        <td key={col.id}
                          className={idx === 0 ? 'dt-sticky-col' : undefined}
                          style={{
                          padding: '8px 10px',
                          textAlign: meta.align === 'right' ? 'right' : 'left',
                          background: meta.cellBg || '#f3f4f6',
                          whiteSpace: 'nowrap'
                        }}>
                          {f
                            ? (typeof f === 'function' ? f({ column: col, table }) : f)
                            : ''}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {table.getAllColumns().some(c => c.columnDef.footerCheck) && (
                  <tr style={{ background: '#fef9c3', borderTop: '1px solid #fde68a', fontWeight: 600 }}>
                    {table.getVisibleLeafColumns().map((col, idx) => {
                      const meta = col.columnDef.meta || {};
                      const f = col.columnDef.footerCheck;
                      return (
                        <td key={col.id} style={{
                          padding: '6px 10px',
                          textAlign: meta.align === 'right' ? 'right' : 'left',
                          background: meta.cellBg ? meta.cellBg : '#fef9c3',
                          color: '#713f12',
                          fontSize: '0.92em',
                          whiteSpace: 'nowrap'
                        }}>
                          {f
                            ? (typeof f === 'function' ? f({ column: col, table }) : f)
                            : ''}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </DndContext>
      </div>
    </div>
  );
}
